import { useCallback, useEffect, useState, useSyncExternalStore } from 'react';
import type {
  Account,
  AdminSession,
  AuthResult,
  CheckoutResponse,
  CreateSessionResult,
  HealthResponse,
} from '@wallflower/shared';
import { ApiError, request } from './api.ts';

/**
 * localStorage, unlike the ops token next door in `opsApi.ts`. That one reaches
 * every event on the instance and should die with the tab; this one is a host's
 * identity, and an app that signs you out every time you close the browser is an
 * app nobody wants to run an event with.
 */
const TOKEN_KEY = 'wallfeed:login';

interface StoredLogin {
  token: string;
  expiresAt: number;
}

export function readLogin(): StoredLogin | null {
  const raw = localStorage.getItem(TOKEN_KEY);
  if (!raw) return null;
  try {
    const stored = JSON.parse(raw) as StoredLogin;
    // The server enforces the real expiry; this only avoids a pointless round trip.
    if (!stored.token || stored.expiresAt <= Date.now()) {
      localStorage.removeItem(TOKEN_KEY);
      return null;
    }
    return stored;
  } catch {
    localStorage.removeItem(TOKEN_KEY);
    return null;
  }
}

export function writeLogin(stored: StoredLogin): void {
  localStorage.setItem(TOKEN_KEY, JSON.stringify(stored));
}

export function clearLogin(): void {
  localStorage.removeItem(TOKEN_KEY);
}

/** Attaches the login, and drops it the moment the server stops accepting it. */
async function accountRequest<T>(path: string, init?: RequestInit): Promise<T> {
  const stored = readLogin();
  try {
    return await request<T>(path, {
      ...init,
      headers: { ...init?.headers, ...(stored ? { 'x-login-token': stored.token } : {}) },
    });
  } catch (err) {
    // An expired login has to reach the account bar too, or it keeps claiming
    // you are signed in on every page until the next reload.
    if (err instanceof ApiError && err.status === 401) {
      clearLogin();
      publish(false);
    }
    throw err;
  }
}

export const accountApi = {
  register(email: string, password: string, name?: string): Promise<{ ok: true }> {
    return request('/api/account/register', {
      method: 'POST',
      body: JSON.stringify({ email, password, ...(name ? { name } : {}) }),
    });
  },

  resendVerification(email: string): Promise<{ ok: true }> {
    return request('/api/account/resend-verification', {
      method: 'POST',
      body: JSON.stringify({ email }),
    });
  },

  verify(token: string): Promise<AuthResult> {
    return request('/api/account/verify', { method: 'POST', body: JSON.stringify({ token }) });
  },

  signIn(email: string, password: string): Promise<AuthResult> {
    return request('/api/account/login', {
      method: 'POST',
      body: JSON.stringify({ email, password }),
    });
  },

  forgot(email: string): Promise<{ ok: true }> {
    return request('/api/account/forgot', { method: 'POST', body: JSON.stringify({ email }) });
  },

  reset(token: string, password: string): Promise<AuthResult> {
    return request('/api/account/reset', {
      method: 'POST',
      body: JSON.stringify({ token, password }),
    });
  },

  signOut(): Promise<{ ok: true }> {
    return accountRequest('/api/account/logout', { method: 'POST' });
  },

  me(): Promise<{ account: Account }> {
    return accountRequest('/api/account/me');
  },

  sessions(): Promise<{ sessions: AdminSession[] }> {
    return accountRequest('/api/account/sessions');
  },

  createSession(name: string): Promise<CreateSessionResult> {
    return accountRequest('/api/account/sessions', {
      method: 'POST',
      body: JSON.stringify({ name }),
    });
  },

  claimSession(adminUrl: string): Promise<AdminSession> {
    return accountRequest('/api/account/sessions/claim', {
      method: 'POST',
      body: JSON.stringify({ adminUrl }),
    });
  },

  deleteSession(sessionId: string): Promise<{ ok: true }> {
    return accountRequest(`/api/account/sessions/${sessionId}`, { method: 'DELETE' });
  },

  checkout(sessionId: string): Promise<CheckoutResponse> {
    return accountRequest(`/api/account/sessions/${sessionId}/checkout`, { method: 'POST' });
  },
};

/** Stores the login a sign-in handed back, so every page agrees on who you are. */
export function acceptAuth(result: AuthResult): Account {
  writeLogin(result.login);
  publish(result.account);
  return result.account;
}

export type AccountState = Account | null | false;

/**
 * One answer to "who is signed in", shared by everyone who asks. Still not a
 * React context - a module-level store with `useSyncExternalStore` needs no
 * provider - but it does have to be shared now that the account bar is on every
 * page: signing out from the bar has to reach the page underneath it, and two
 * components asking the same question should not cost two round trips.
 *
 * `null` while the answer is still in flight, `false` when signed out.
 */
let accountState: AccountState = readLogin() ? null : false;
let inFlight = false;
const listeners = new Set<() => void>();

function publish(next: AccountState): void {
  accountState = next;
  for (const listener of listeners) listener();
}

function subscribe(listener: () => void): () => void {
  listeners.add(listener);
  return () => {
    listeners.delete(listener);
  };
}

/** Resolves the stored login into an account, at most once at a time. */
function loadAccount(): void {
  if (!readLogin()) {
    if (accountState !== false) publish(false);
    return;
  }
  if (inFlight) return;
  inFlight = true;
  accountApi
    .me()
    .then((result) => publish(result.account))
    .catch(() => publish(false))
    .finally(() => {
      inFlight = false;
    });
}

export function useAccount(): {
  account: AccountState;
  signOut: () => void;
} {
  const account = useSyncExternalStore(subscribe, () => accountState);

  useEffect(() => {
    if (accountState === null) loadAccount();
  }, []);

  const signOut = useCallback(() => {
    // Fire and forget: the local token is gone either way, and a failed revoke
    // should not keep someone looking signed in.
    void accountApi.signOut().catch(() => undefined);
    clearLogin();
    publish(false);
  }, []);

  return { account, signOut };
}

/**
 * What this instance can do and what it charges, so pages need not probe
 * endpoints for 404s or hardcode terms the operator can change. Null while it is
 * still loading, which the landing page uses to avoid flashing an empty price.
 */
export function useCapabilities(): HealthResponse | null {
  const [capabilities, setCapabilities] = useState<HealthResponse | null>(null);

  useEffect(() => {
    let cancelled = false;
    request<HealthResponse>('/api/health')
      .then((health) => {
        if (!cancelled) setCapabilities(health);
      })
      .catch(() => {
        // Unreachable is not the same as free: keep the pay button off and the
        // price unquoted rather than guessing at either.
        if (!cancelled) {
          setCapabilities({
            ok: false,
            uptime: 0,
            payments: false,
            pricing: { amount: null, currency: null, mediaLimit: 0, ttlDays: 0 },
          });
        }
      });
    return () => {
      cancelled = true;
    };
  }, []);

  return capabilities;
}
